import { Suspense } from 'react'
import { type Metadata } from 'next'
import Link from 'next/link'
import { breadcrumbSchema } from '@entities/fulfillment'
import { JsonLd } from '@ui/JsonLd'
import {
  faqPageSchema,
  LandingAnchorNav,
  LandingBenefits,
  LandingFaq,
  LandingFigureRowPlaceholder,
  LandingFinalCall,
  LandingSteps,
  landingType,
} from '@ui/landing'

import { requestLandingPath, requestWizardPath } from '../config'
import { RequestCatalogNumbers } from './request-catalog-numbers'
import { benefits, questions, sections, steps } from './request-content'
import { RequestHero } from './request-hero'
import { RequestRoutingScheme } from './request-routing-scheme'

const title = 'Заявка на просчёт фулфилмента — бесплатно'
const description =
  'Опишите задачу один раз — заявку получат все подходящие фулфилменты и ответят своей ценой. Бесплатно: ни за заявку, ни за ответы платить не нужно.'

export const requestLandingMetadata: Metadata = {
  title,
  description,
  alternates: { canonical: requestLandingPath },
  openGraph: { title, description, url: requestLandingPath },
}

export const FulfillmentRequestLandingPage = () => (
  <div className="text-foreground w-full bg-white">
    <JsonLd
      data={breadcrumbSchema([
        { name: 'Все фулфилменты', path: '/fulfillment' },
        { name: 'Заявка на просчёт', path: requestLandingPath },
      ])}
    />
    <JsonLd data={faqPageSchema(questions)} />

    <LandingAnchorNav actionHref={requestWizardPath} actionLabel="Оставить заявку" sections={sections} />

    <div className={landingType.container}>
      <nav
        aria-label="Хлебные крошки"
        className={`${landingType.noteText} ${landingType.mutedText} flex flex-wrap items-center gap-2 pt-6`}
      >
        <Link href="/fulfillment" className="underline-offset-4 hover:underline">
          Все фулфилменты
        </Link>
        <span aria-hidden="true">\</span>
        <span aria-current="page">Заявка на просчёт</span>
      </nav>

      <RequestHero />

      <Suspense fallback={<LandingFigureRowPlaceholder />}>
        <RequestCatalogNumbers />
      </Suspense>

      <LandingSteps
        id="how-it-works"
        title="Как это работает"
        intro="Три шага: описали задачу — получили цены. Без обзвона по чатам и без оплаты."
        items={steps}
      />

      <div className="pb-14 lg:pb-20">
        <RequestRoutingScheme />
      </div>
    </div>

    <LandingBenefits
      id="benefits"
      title="Что даёт заявка"
      intro="Условия задачи вы описываете один раз, а считают их несколько фулфилментов сразу. Ответы приходят в одно место, и их видно рядом."
      items={benefits}
    />

    <div className={landingType.container}>
      <LandingFaq id="faq" title="Частые вопросы" items={questions} />
    </div>

    <LandingFinalCall
      title="Оставьте заявку — цены придут к вам"
      text="Опишите задачу один раз вместо обзвона по чатам. Заявка, ответы фулфилментов и сам каталог бесплатны, а закрыть заявку можно в любой момент — когда исполнитель найдётся."
      action={{ href: requestWizardPath, label: 'Оставить заявку бесплатно' }}
      secondary={{ href: '/fulfillment', label: 'Посмотреть каталог' }}
      note="Бесплатно · без входа · без обзвона"
    />
  </div>
)
